import SimpleTable, {
	HeadersType,
} from "../../../../common/components/SimpleTable/SimpleTable";
import { useEffect, useMemo, useState } from "react";
import {
	useLazyGetDealsQuery,
	useSyncContractPeriodBCVMutation,
	useSyncContractPeriodBookDealsMutation,
	useSyncContractPeriodCompensationBCVMutation,
	useSyncContractPeriodCompensationBookDealsMutation,
} from "../../../../requests_cm/gecoContractsService/service";
import { useRtkQueryDynamicEndpoint } from "../../../../common/hooks/useRtkQueryDynamicEndpoint";
import CircularProgress from "@mui/material/CircularProgress";
import { Spacer } from "../../../../common/components/Spacer";
import DealTabCancelButton from "./DealTabCancelButton";
import DealTabStatusButton from "./DealTabStatusButton";
import { ContractPeriodDealsType } from "./DealsTab.type";
import { Box } from "@mui/material";
import { DealType } from "../../../../requests_cm/gecoContractsService/types";
import { useIsUserWithinGroups } from "../../../../common/hooks/useIsUserWithinGroups";
import { Groups } from "../../../authentication/authentication.constant";
import NoDataMessage from "../../../../common/components/NoDataMessage";
import Tag from "../../../../common/components/Tag/Tag";
import { FluidButton } from "../../../../common/components/FluidButton";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { OrderEnum, PageInfoType } from "../../../../requests_cm/gecoTypes";
import _ from "lodash";

const DealsTab = ({ contractPeriodId }: { contractPeriodId: number }) => {
	const { isUserAuthorized } = useIsUserWithinGroups();
	const [getDealsBase, { data: dataDeals, isLoading: isLoadingDeals }] =
		useLazyGetDealsQuery();
	const { t, i18n } = useTranslation();
	const [
		syncContractPeriodBcvBase,
		{
			isSuccess: isSyncBcvSuccess,
			isError: isSyncBcvError,
			reset: resetSyncBcv,
		},
	] = useSyncContractPeriodBCVMutation();
	const [
		syncContractPeriodDealsBase,
		{
			isSuccess: isSyncDealsSuccess,
			isError: isSyncDealsError,
			reset: resetSyncDeals,
		},
	] = useSyncContractPeriodBookDealsMutation();
	const [
		syncContractPeriodCompensationBcvBase,
		{
			isSuccess: isSyncCompensationBcvSuccess,
			isError: isSyncCompensationBcvError,
			reset: resetSyncCompensationBcv,
		},
	] = useSyncContractPeriodCompensationBCVMutation();
	const [
		syncContractPeriodCompensationDealsBase,
		{
			isSuccess: isSyncCompensationDealsSuccess,
			isError: isSyncCompensationDealsError,
			reset: resetSyncCompensationDeals,
		},
	] = useSyncContractPeriodCompensationBookDealsMutation();

	const syncContractPeriodBcv = useRtkQueryDynamicEndpoint(
		syncContractPeriodBcvBase
	);
	const syncContractPeriodDeals = useRtkQueryDynamicEndpoint(
		syncContractPeriodDealsBase
	);
	const syncContractPeriodCompensationBcv = useRtkQueryDynamicEndpoint(
		syncContractPeriodCompensationBcvBase
	);
	const syncContractPeriodCompensationDeals = useRtkQueryDynamicEndpoint(
		syncContractPeriodCompensationDealsBase
	);

	const getDeals = useRtkQueryDynamicEndpoint(getDealsBase);

	const isUserAuthorizedBool = isUserAuthorized([
		Groups.geco_admin,
		Groups.geco_trader,
	]);

	useEffect(() => {
		getDeals({ contract_period_id: contractPeriodId }, false);

		const interval = setInterval(() => {
			getDeals({ contract_period_id: contractPeriodId }, false);
		}, 5000);

		return () => clearInterval(interval);
	}, [contractPeriodId]);

	useEffect(() => {
		if (
			isSyncBcvSuccess ||
			isSyncDealsSuccess ||
			isSyncCompensationDealsSuccess ||
			isSyncCompensationBcvSuccess
		) {
			enqueueSnackbar(`Sync dispatched successfully`, {
				variant: "success",
				autoHideDuration: 2000,
			});
			resetSyncBcv();
			resetSyncDeals();
			resetSyncCompensationDeals();
			resetSyncCompensationBcv();
		}
	}, [
		isSyncBcvSuccess,
		isSyncDealsSuccess,
		isSyncCompensationDealsSuccess,
		isSyncCompensationBcvSuccess,
	]);

	useEffect(() => {
		if (
			isSyncBcvError ||
			isSyncDealsError ||
			isSyncCompensationDealsError ||
			isSyncCompensationBcvError
		) {
			enqueueSnackbar(`Could not dispatch sync`, {
				variant: "error",
				autoHideDuration: 2000,
			});
			resetSyncBcv();
			resetSyncDeals();
			resetSyncCompensationDeals();
			resetSyncCompensationBcv();
		}
	}, [
		isSyncBcvError,
		isSyncDealsError,
		isSyncCompensationDealsError,
		isSyncCompensationBcvError,
	]);

	let headers: HeadersType<ContractPeriodDealsType>[] = [
		{ label: "Trade ID", accessor: "trade_id", sortable: true },
		{ label: "Year", accessor: "year", sortable: true },
		{
			label: "Start Date",
			accessor: "start_date",
			sortable: true
		},
		{
			label: "End Date",
			accessor: "end_date",
			sortable: true
		},
		{ label: "Deal Type", accessor: "deal_type", sortable: true },
		{ label: "Deal Status", accessor: "deal_status", sortable: true },
		{ label: "Curtailment Object", accessor: "curtailment_object", sortable: true },
		{ label: "Actions", accessor: "actions" },
	];
	headers = headers.filter(
		(header) => header.accessor !== "actions" || isUserAuthorizedBool
	);

	const translateDealType = (value: string) => {
		const key = `contract_periods.deals.${value}`;
		if (i18n.exists(key)) {
			return t(key);
		}

		return value;
	};

	const [pageInfo, setPageInfo] = useState<PageInfoType>({page: 1, sortOn: "deal_type", order: OrderEnum.ASC});

	const dealsObjects = useMemo(() => {
		let sortedResult = dataDeals;
		if (pageInfo?.sortOn) {
			sortedResult = _.sortBy(dataDeals, pageInfo.sortOn);
			if (pageInfo.order === OrderEnum.DESC) {
				sortedResult =  sortedResult.reverse();
			}
		}

		const res = sortedResult?.map((deal) => ({
			...deal,
			deal_type: <Tag label={translateDealType(deal.deal_type)} size="small" />,
			deal_status: <DealTabStatusButton deal={deal} />,
			curtailment_object: deal.curtailment_object_name,
			actions:
				isUserAuthorizedBool && deal.trade_id ? (
					<DealTabCancelButton
						contractPeriodId={contractPeriodId}
						dealId={deal.id}
						isCompensation={deal.deal_type?.includes(
							DealType.COMPENSATION
						)}
					/>
				) : null,
		})) as ContractPeriodDealsType[];
		return res;
	}, [dataDeals, pageInfo]);

	const [selectedRows, setSelectedRows] = useState<string[]>([]);
	const [dealSelection, setDealSelection] = useState<ContractPeriodDealsType[]>([]);

	const selectedDeals = (rows: string[]) => {
		const selection: ContractPeriodDealsType[] = [];
		rows.forEach((id: string) => {
			const deal = dealsObjects.find((deal) => deal.id === Number(id))
			if (deal) {
				selection.push(deal);
			}
		});

		setDealSelection(selection);
		setSelectedRows(rows);
	};

	return (
		<>
			<Spacer gap={24} />
			{isLoadingDeals && (
				<Box sx={{ display: "flex", justifyContent: "center" }}>
					<CircularProgress />
				</Box>
			)}
			{!isLoadingDeals && (
				<>
					{dataDeals && !dealsObjects?.length && <NoDataMessage />}
					{!!dealsObjects?.length && (
						<>
							<Box
								sx={{
									display: "flex",
									gap: "16px",
									marginBottom: "24px",
								}}
							>
								<FluidButton
									icon="sync"
									label={`Sync ${t("contract_periods.deals.POWER_GOO")} Deals`}
									emphasis="subtle"
									onClick={() =>
										syncContractPeriodDeals({
											contract_period_id:
												contractPeriodId,
										})
									}
									isDisabled={!isUserAuthorizedBool}
								/>
								<FluidButton
									icon="sync"
									label={`Sync ${t("contract_periods.deals.POWER_GOO")} BCV(s)`}
									emphasis="subtle"
									onClick={() =>
										syncContractPeriodBcv({
											contract_period_id:
												contractPeriodId,
										})
									}
									isDisabled={!isUserAuthorizedBool}
								/>
								<FluidButton
									icon="sync"
									label="Sync Compensation Deals"
									emphasis="subtle"
									onClick={() =>
										syncContractPeriodCompensationDeals({
											contract_period_id:
												contractPeriodId,
										})
									}
									isDisabled={!isUserAuthorizedBool}
								/>
								<FluidButton
									icon="sync"
									label="Sync Compensation BCV(s)"
									emphasis="subtle"
									onClick={() =>
										syncContractPeriodCompensationBcv({
											contract_period_id:
												contractPeriodId,
										})
									}
									isDisabled={!isUserAuthorizedBool}
								/>
								<FluidButton
									icon="content_copy"
									label="Copy selected trade id(s)"
									emphasis="subtle"
									onClick={async () => {
										const tradeIds: number[] = [];
										dealSelection.forEach((deal) => {
											if (deal.trade_id) {
												tradeIds.push(deal.trade_id);
											}
										});
										const text = tradeIds.join("|");
										return await navigator.clipboard.writeText(text);
									}}
									isDisabled={dealSelection?.length === 0}
								/>
							</Box>

							<SimpleTable
								dataTestid="clickabilityTable"
								headers={headers}
								items={dealsObjects}
								infiniteScroll
								selectableRows={true}
								selectedRows={selectedRows}
								setSelectedRows={selectedDeals}
								setPageInfo={setPageInfo}
								sortsLocally={true}
							/>
						</>
					)}
				</>
			)}
		</>
	);
};

export default DealsTab;
